import { Abi, Chain, erc20Abi } from "viem";

// etherem abis
import GainAdapterETH from "@abi/GainAdapterETH.json";
import elMerkleDistributorAbi from "@abi/ethereum/ELMerkleDistributor.json";
import elTokenABI from "@abi/ethereum/ELToken.json";
import kepAbi from "@abi/ethereum/KEP.json";
import lrtDepositPoolAbi from "@abi/ethereum/LRTDepositPool.json";
import unstakingVaultAbi from "@abi/ethereum/LRTUnstakingVault.json";
import withdrawalManagerAbi from "@abi/ethereum/LRTWithdrawalManager.json";
import merkleDistributorAbi from "@abi/ethereum/MerkleDistributor.json";
import rsETHAdapterJson from "@abi/rsETHAdapter.json";
import ScrollDistributorAbi from "@abi/scroll/ScrollMerkleDistributor.json";
import lrtDepositPoolAbiXL from "@abi/xlayer/LRTDepositPool.json";
import ethVaultJson from "@abi/vault/eth.json";
import gainVaultJson from "@abi/vault/gain.json";
import otherVaultJson from "@abi/vault/other.json";

// arbitrum abis
import lrtDepositPoolAbiArb from "@abi/arbitrum/LRTDepositPool.json";

// common abi for blast scroll optimism
import lrtDepositPoolAbiCommon from "@abi/common/LRTDepositPool.json";

// manta abis
import tokenWrapperAbi from "@abi/common/wrseth.json";

// xlayer abis

import { Hash } from "viem";
import {
  arbitrum,
  base,
  blast,
  linea,
  mainnet,
  manta,
  mode,
  optimism,
  scroll,
  xLayer,
  zircuit,
  zkSync,
} from "viem/chains";

export const ETHX = "ETHx";
export const STETH = "stETH";
export const SFRXETH = "sfrxETH";
export const ETH = "ETH";
export const OKB = "OKB";
export const WETH = "wETH";
export const WSTETH = "wstETH";
export const RSETH = "rsETH";
export const WRSETH = "wrsETH";
export const WAGETH = "agETH";
export const AGETH = "agETH.axl";
export const HGETH = "hgETH";

export const VAULT_ALL_TOKENS = {
  eETH: "eETH",
  mETH: "mETH",
  rETH: "rETH",
  stETH: "stETH",
  swETH: "swETH",
  weETH: "weETH",
  wstETH: "wstETH",
};

export const enum TAGS {
  KELP_MILES = "kelpMiles",
  EL_POINTS = "elPoints",
  EXTRA_EL_POINTS = "extraELPoints",
}

export const LST_TAGS = {
  [TAGS.KELP_MILES]: {
    title: "Kernel Points",
    style: {
      backgroundColor: "#5D8A8A",
    },
  },
  [TAGS.EL_POINTS]: {
    title: "EL Points",
    style: {
      backgroundColor: "#78A5CF",
    },
  },
  [TAGS.EXTRA_EL_POINTS]: {
    title: "+25 EL Points/rsETH",
    style: {
      backgroundColor: "#DF60AC",
    },
  },
};

export type TokenDetails = {
  [x: string]: {
    symbol: string;
    decimals: number;
    address: Hash;
    abi: Abi;
    priority?: number;
    unstakePriority?: number;
    tags: Array<TAGS> | [];
    isLiquidStakeSupported?: boolean;
  };
};

export type ContractDetails = {
  [x: string]: {
    address: Hash;
    abi: Abi;
  };
};

export type ContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPools: ContractDetails;
    tokens: TokenDetails;
    nativeToken: {
      symbol: string;
      decimals: number;
      address: string;
      tags: Array<TAGS> | [];
      isRestakeSupported: boolean;
      unstakePriority?: number;
      isVaultSupported?: boolean;
      isYieldVaultSupported?: boolean;
    };
    xtoken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
  };
};

export const ledgerSupportedChains = [mainnet, arbitrum, optimism];

export const contractConfig: ContractsConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      unstakePriority: 0,
      isVaultSupported: true,
      isYieldVaultSupported: true,
    },
    xtoken: {
      address: "0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7",
      abi: erc20Abi,
      symbol: "rsETH",
      decimals: 18,
    },

    tokens: {
      [STETH]: {
        address: "0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84",
        abi: erc20Abi,
        symbol: "stETH",
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        unstakePriority: -1,
      },
      [ETHX]: {
        address: "0xA35b1B31Ce002FBF2058D22F30f95D405200A15b",
        abi: erc20Abi,
        symbol: "ETHx",
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        priority: -1,
        isLiquidStakeSupported: true,
      },
      [SFRXETH]: {
        address: "0xac3e018457b222d93114458476f3e3416abbe38f",
        abi: erc20Abi,
        symbol: "sfrxETH",
        decimals: 18,
        tags: [],
        unstakePriority: -2,
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x036676389e48133b63a802f8635ad39e752d375d",
        abi: lrtDepositPoolAbi as Abi,
      },
      kep: {
        address: "0x8E3A59427B1D87Db234Dd4ff63B25E4BF94672f4",
        abi: kepAbi as Abi,
      },
      merkleDistributor: {
        address: "0x2DDB11443bD9Ceb92d4951A05f55eb7096EB53d3",
        abi: merkleDistributorAbi as Abi,
      },
      elToken: {
        address: "0xec53bF9167f50cDEB3Ae105f56099aaaB9061F83",
        abi: elTokenABI as Abi,
      },
      elMerkleDistributor: {
        address: "0xc135b516e399C1ed702588D887FBBE6F2d1bA27A",
        abi: elMerkleDistributorAbi as Abi,
      },
      elPkMerkleDistributor: {
        address: "0x9bB6d4b928645EdA8f9C019495695BA98969eFF1",
        abi: elMerkleDistributorAbi as Abi,
      },
      lrtWithdrawalManager: {
        address: "0x62De59c08eB5dAE4b7E6F7a8cAd3006d6965ec16",
        abi: withdrawalManagerAbi as Abi,
      },
      lrtUnstakingVault: {
        address: "0xc66830E2667bc740c0BED9A71F18B14B8c8184bA",
        abi: unstakingVaultAbi as Abi,
      },
    },
  },
  [arbitrum.id]: {
    chain: arbitrum,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      isVaultSupported: true,
    },
    xtoken: {
      address: "0x4186bfc76e2e237523cbc30fd220fe055156b41f",
      abi: erc20Abi,
      symbol: "rsETH",
      decimals: 18,
    },

    tokens: {
      [WSTETH]: {
        address: "0x5979D7b546E38E414F7E9822514be443A4800529",
        abi: erc20Abi,
        symbol: WSTETH,
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        unstakePriority: -1,
      },
      [ETHX]: {
        address: "0xED65C5085a18Fa160Af0313E60dcc7905E944Dc7",
        abi: erc20Abi,
        symbol: ETHX,
        decimals: 18,
        tags: [TAGS.KELP_MILES],
        priority: -1,
        isLiquidStakeSupported: true,
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x376A7564AF88242D6B8598A5cfdD2E9759711B61",
        abi: lrtDepositPoolAbiArb as Abi,
      },
    },
  },
  [mode.id]: {
    chain: mode,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xbDf612E616432AA8e8D7d8cC1A9c934025371c5C",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [blast.id]: {
    chain: blast,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x1558959f1a032F83f24A14Ff539944A926C51bdf",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      isVaultSupported: true,
    },
    xtoken: {
      address: "0xa25b25548B4C98B0c7d3d27dcA5D5ca743d68b7F",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xb80deaecd7F4Bca934DE201B11a8711644156a0a",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
      scrollDistributor: {
        address: "0xbE7E2d809E2C7405B5972292986324a798921D98",
        abi: ScrollDistributorAbi as Abi,
      },
    },
  },
  [optimism.id]: {
    chain: optimism,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0x87eEE96D50Fb761AD85B1c982d28A042169d61b1",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xaAA687e218F9B53183A6AA9639FBD9D6e69EcB73",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [base.id]: {
    chain: base,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0xEDfa23602D0EC14714057867A78d01e94176BEA0",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x291088312150482826b3A37d5A69a4c54DAa9118",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
      isVaultSupported: true,
    },
    xtoken: {
      address: "0xD2671165570f41BBB3B0097893300b6EB6101E6C",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },

    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x057297e44a3364139edcf3e1594d6917ed7688c2",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [zkSync.id]: {
    chain: zkSync,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0xd4169E045bcF9a86cC00101225d9ED61D2F51af2",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0x41b300f5A619973b20931f0944C85DB229d5E27f",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
  [xLayer.id]: {
    chain: xLayer,
    nativeToken: {
      symbol: OKB,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: false,
    },
    xtoken: {
      address: "0x5A71f5888EE05B36Ded9149e6D32eE93812EE5e9",
      abi: erc20Abi,
      symbol: "wrsETH",
      decimals: 18,
    },
    tokens: {
      [WETH]: {
        address: "0x5A77f1443D16ee5761d310e38b62f77f726bC71c",
        abi: erc20Abi,
        symbol: WETH,
        decimals: 18,
        tags: [],
      },
    },
    interactionPools: {
      lrtDepositPool: {
        address: "0x4Ef626efE4a3A279a9DC7e7a91C1c9CaaAE8e159",
        abi: lrtDepositPoolAbiXL as Abi,
      },
    },
  },
  [zircuit.id]: {
    chain: zircuit,
    nativeToken: {
      symbol: ETH,
      decimals: 18,
      address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee",
      tags: [TAGS.KELP_MILES],
      isRestakeSupported: true,
    },
    xtoken: {
      address: "0x311a51Ff8839B6afcAA9426BdBffDF2e70A0dA25",
      abi: erc20Abi,
      symbol: WRSETH,
      decimals: 18,
    },
    tokens: {},
    interactionPools: {
      lrtDepositPool: {
        address: "0xca276450b2c26061785CF11668dd481168E102Cb",
        abi: lrtDepositPoolAbiCommon as Abi,
      },
    },
  },
};

type SwapTokenDetails = {
  symbol: string;
  decimals: number;
  address: Hash;
  abi: Abi;
};

export type SwapContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    tokens: {
      rsETH: SwapTokenDetails;
      wrsETH: SwapTokenDetails;
    };
  };
};

export type VaultSwapContractsConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    tokens: {
      agETH: SwapTokenDetails;
      wagETH: SwapTokenDetails;
    };
  };
};

export type VaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    rsETHAdapterPool?: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
  };
};
export type YieldVaultConfigMap = {
  [x: Chain["id"]]: {
    chain: Chain;
    interactionPool: { address: Hash; abi: Abi };
    gainAdapterETH: { address: Hash; abi: Abi };
    xToken: {
      symbol: string;
      decimals: number;
      address: Hash;
      abi: Abi;
    };
    tokens: {
      [x: string]: {
        symbol: string;
        decimals: number;
        address: Hash;
        abi: Abi;
        priority?: number;
        isLiquidStakeSupported?: boolean;
        tags?: Array<TAGS> | [];
      };
    };
  };
};

export const vaultsContractConfig: VaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0xe1B4d34E8754600962Cd944B535180Bd758E6c2e",
      abi: ethVaultJson as Abi,
    },
    rsETHAdapterPool: {
      address: "0xbf28c9fcb12a97441488f9c68faa49811a98688a",
      abi: rsETHAdapterJson as Abi,
    },
    xToken: {
      address: "0xe1B4d34E8754600962Cd944B535180Bd758E6c2e",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [arbitrum.id]: {
    chain: arbitrum,
    interactionPool: {
      address: "0x77F5979D8eA6d72d6C6C451eC23c772D68211c5f",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0xF1A88250532a4A66A2420a8cbB434Da82E1E2cA1",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [scroll.id]: {
    chain: scroll,
    interactionPool: {
      address: "0x6c5513F8701a6E58C82D9a0585A2E533A7fC773b",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0xd44605d3E5eF9A73379Ce5258B06e4383c6FF32a",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
  [linea.id]: {
    chain: linea,
    interactionPool: {
      address: "0x7F260B785E3B74155a39d82251B47D05ae0d6c61",
      abi: otherVaultJson as Abi,
    },
    xToken: {
      address: "0x2a4f1dcc79b83608f9e3BC1F3F55fBEfCBFaE885",
      abi: erc20Abi,
      symbol: "agETH",
      decimals: 18,
    },
  },
};

export const yieldVaultContractConfig: YieldVaultConfigMap = {
  [mainnet.id]: {
    chain: mainnet,
    interactionPool: {
      address: "0xc824A08dB624942c5E5F330d56530cD1598859fD",
      abi: gainVaultJson as Abi,
    },
    gainAdapterETH: {
      address: "0xB185D98056419029daE7120EcBeFa0DbC12c283A",
      abi: GainAdapterETH as Abi,
    },
    xToken: {
      address: "0xc824A08dB624942c5E5F330d56530cD1598859fD",
      abi: erc20Abi,
      symbol: "hgETH",
      decimals: 18,
    },
    tokens: {
      [VAULT_ALL_TOKENS.eETH]: {
        address: "0x35fA164735182de50811E8e2E824cFb9B6118ac2",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.eETH,
        decimals: 18,
      },
      [VAULT_ALL_TOKENS.rETH]: {
        address: "0xae78736Cd615f374D3085123A210448E74Fc6393",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.rETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.stETH]: {
        address: "0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.stETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.swETH]: {
        address: "0xf951E335afb289353dc249e82926178EaC7DEd78",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.swETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.weETH]: {
        address: "0xCd5fE23C85820F7B72D0926FC9b05b43E359b7ee",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.weETH,
        decimals: 18,
        priority: -1,
      },
      [VAULT_ALL_TOKENS.wstETH]: {
        address: "0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0",
        abi: erc20Abi,
        symbol: VAULT_ALL_TOKENS.wstETH,
        decimals: 18,
        priority: -1,
      },
    },
  },
};

export const vaultSwapContractsConfig: VaultSwapContractsConfigMap = {
  [arbitrum.id]: {
    chain: arbitrum,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0xF1A88250532a4A66A2420a8cbB434Da82E1E2cA1",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0xd44605d3E5eF9A73379Ce5258B06e4383c6FF32a",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    tokens: {
      agETH: {
        address: "0x1bD0Fe8E92a157D3ef66C9FB9e38621252b407c2",
        abi: erc20Abi,
        symbol: "agETH",
        decimals: 18,
      },
      wagETH: {
        address: "0x2a4f1dcc79b83608f9e3BC1F3F55fBEfCBFaE885",
        abi: tokenWrapperAbi as Abi,
        symbol: "wagETH",
        decimals: 18,
      },
    },
  },
};

export const swappingContractsConfig: SwapContractsConfigMap = {
  [manta.id]: {
    chain: manta,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x9dd4f9EeE9B05D1ebec1d4aAE7Ae9F5d8D235CD4",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [mode.id]: {
    chain: mode,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [blast.id]: {
    chain: blast,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xe7903B1F75C534Dd8159b313d92cDCfbC62cB3Cd",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [scroll.id]: {
    chain: scroll,
    tokens: {
      rsETH: {
        address: "0x65421ba909200b81640d98B979d07487C9781B66",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xa25b25548B4C98B0c7d3d27dcA5D5ca743d68b7F",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [optimism.id]: {
    chain: optimism,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0x87eEE96D50Fb761AD85B1c982d28A042169d61b1",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [base.id]: {
    chain: base,
    tokens: {
      rsETH: {
        address: "0x1Bc71130A0e39942a7658878169764Bbd8A45993",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xEDfa23602D0EC14714057867A78d01e94176BEA0",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [linea.id]: {
    chain: linea,
    tokens: {
      rsETH: {
        address: "0x4186bfc76e2e237523cbc30fd220fe055156b41f",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xD2671165570f41BBB3B0097893300b6EB6101E6C",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  [zkSync.id]: {
    chain: zkSync,
    tokens: {
      rsETH: {
        address: "0x6bE2425C381eb034045b527780D2Bf4E21AB7236",
        abi: erc20Abi,
        symbol: "rsETH",
        decimals: 18,
      },
      wrsETH: {
        address: "0xd4169E045bcF9a86cC00101225d9ED61D2F51af2",
        abi: tokenWrapperAbi as Abi,
        symbol: "wrsETH",
        decimals: 18,
      },
    },
  },
  // [xLayer.id]: {
  //   chain: xLayer,
  //   tokens: {
  //     rsETH: {
  //       address: "0x4Ef626efE4a3A279a9DC7e7a91C1c9CaaAE8e159",
  //       abi: erc20Abi,
  //       symbol: "rsETH",
  //       decimals: 18,
  //     },
  //     wrsETH: {
  //       address: "0x5A71f5888EE05B36Ded9149e6D32eE93812EE5e9",
  //       abi: tokenWrapperAbi as Abi,
  //       symbol: "wrsETH",
  //       decimals: 18,
  //     },
  //   },
  // },
  [zircuit.id]: {
    chain: zircuit,
    tokens: {
      rsETH: {
        address: "0x4186BFC76E2E237523CBC30FD220FE055156b41F",
        abi: erc20Abi,
        symbol: RSETH,
        decimals: 18,
      },
      wrsETH: {
        address: "0x311a51Ff8839B6afcAA9426BdBffDF2e70A0dA25",
        abi: tokenWrapperAbi as Abi,
        symbol: WRSETH,
        decimals: 18,
      },
    },
  },
};
